import { t } from '@/shared/i18n';
import { z } from 'zod';
import { validZoneList } from '@/features/calendar/zoneList';
import { LANGUAGE_CODES, validLangs } from '@/features/translate/languages';

/**
 * Личные настройки интерфейса. Хранятся на сервере (одинаковы на всех устройствах человека);
 * описание повторяет App\Identity\Domain\Preferences — допустимые значения и значения по умолчанию.
 */
export const PREFERENCE_SCHEMA = {
  enterSends: { values: null, initial: true },
  density: { values: ['comfortable', 'compact'], initial: 'comfortable' },
  markRead: { values: ['open', 'newest', 'manual'], initial: 'open' },
  sidebarSort: { values: ['alpha', 'recent'], initial: 'alpha' },
  showUnreads: { values: null, initial: true },
  showThreads: { values: null, initial: true },
  showDrafts: { values: null, initial: true },
  jumbomoji: { values: null, initial: true },
  emoticons: { values: null, initial: true },
  formattingBar: { values: null, initial: true },
  markup: { values: null, initial: false },
  codeBlockEnter: { values: null, initial: true },
  language: { values: ['ru', 'en'], initial: 'ru' },
  notifyThreads: { values: null, initial: true },
  notifyHuddles: { values: null, initial: true },
  notifyKeywords: { values: 'text', initial: '' },
  notifySchedule: { values: ['always', 'everyday', 'weekdays'], initial: 'always' },
  notifyFrom: { values: 'time', initial: '09:00' },
  notifyTo: { values: 'time', initial: '19:00' },
  notifyPreview: { values: null, initial: true },
  notifySound: { values: ['ding', 'pop', 'chime', 'knock', 'none'], initial: 'ding' },
  notifyInApp: { values: null, initial: true },
  inviteCard: { values: null, initial: true },
  sidebarShow: { values: ['all', 'unread'], initial: 'all' },
  sidebarActivity: { values: ['all', 'week', 'month'], initial: 'all' },
  sidebarGroup: { values: ['sections', 'none'], initial: 'sections' },
  sidebarColor: { values: ['light', 'dark'], initial: 'light' },
  dmView: { values: ['detailed', 'names'], initial: 'detailed' },
  calendarReminder: { values: ['0', '5', '10', '15', '30'], initial: '10' },
  undoSend: { values: ['0', '5', '10', '20', '30'], initial: '10' },
  calendarStatus: { values: null, initial: true },
  messageSize: { values: ['normal', 'large'], initial: 'normal' },
  font: { values: ['passwork', 'lato', 'system'], initial: 'passwork' },
  reactionMode: { values: ['custom', 'frequent'], initial: 'custom' },
  reactionSet: { values: 'text', initial: '' },
  railServices: { values: 'text', initial: '' },
  railItems: { values: 'rail', initial: '' },
  homeCards: { values: 'cards', initial: '' },
  homeRole: { values: ['', 'sales', 'dev', 'support', 'lead'], initial: '' },
  homeWidgets: { values: 'text', initial: '' },
  homePrompts: { values: 'long', initial: '' },
  translateOffer: { values: null, initial: true },
  translateTo: { values: 'lang', initial: '' },
  translateSkip: { values: 'langs', initial: '' },
  calendarZones: { values: 'zones', initial: '' },
  calendarZonesRecent: { values: 'zones', initial: '' },
  sectionGuides: { values: 'text', initial: '' },
  screenshotKeys: { values: 'text', initial: '' },
  screenshotsOff: { values: 'text', initial: '' },
  appKeys: { values: 'text', initial: '' },
  composerTools: { values: 'text', initial: '' },
  paneWidths: { values: 'text', initial: '' },
  circleCamera: { values: ['last', 'user', 'env'], initial: 'last' },
  circleFacing: { values: ['user', 'env'], initial: 'user' },
} as const;

/** Разделы полосы слева, которые можно включать, выключать и переставлять («Главная» всегда первая). */
export const RAIL_ITEMS = ['dms', 'activity', 'todos', 'calendar', 'docs', 'clients', 'assistant', 'awards', 'channels', 'people', 'files', 'screenshots', 'apps'] as const;

const RAIL_SET: ReadonlySet<string> = new Set(RAIL_ITEMS);

export function isRailItem(id: string): id is (typeof RAIL_ITEMS)[number] {
  return RAIL_SET.has(id);
}

/** Каждый раздел — известный и не больше одного раза («dms -todos calendar»). */
export function validRailItems(value: string): boolean {
  return validOrder(value, isRailItem);
}

/** Карточки быстрого доступа на «Главной» телефона, которые можно включать, выключать и переставлять. */
export const HOME_CARDS = ['unreads', 'threads', 'assistant', 'todos', 'calendar', 'drafts', 'saved', 'docs', 'clients', 'channels', 'people', 'awards', 'files', 'apps'] as const;

const CARD_SET: ReadonlySet<string> = new Set(HOME_CARDS);

export function isHomeCard(id: string): id is (typeof HOME_CARDS)[number] {
  return CARD_SET.has(id);
}

/** Строка порядка («a -b c»): каждый пункт — известный и не больше одного раза. */
export function validOrder(value: string, known: (id: string) => boolean): boolean {
  if (value === '') return true;
  if (value.includes('--')) return false;
  const ids = value.split(' ').map((x) => x.replace(/^-/, ''));
  return ids.every(known) && new Set(ids).size === ids.length;
}

/** Карточки «Главной» на телефоне: тот же формат, что у разделов полосы. */
export function validHomeCards(value: string): boolean {
  return validOrder(value, isHomeCard);
}

/** Длина свободного текста в настройках (ключевые слова). */
export const TEXT_LIMIT = 300;
/** Длинный личный текст (свои запросы «Моего дня»). */
export const LONG_LIMIT = 1200;
/** Время с шагом полчаса: «00:00» … «23:30». */
export const HALF_HOURS: readonly string[] = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 === 0 ? '00' : '30'}`);
export type NotifySound = 'ding' | 'pop' | 'chime' | 'knock' | 'none';

export interface Preferences {
  /** Enter отправляет сообщение; иначе — Ctrl+Enter, а Enter переносит строку. */
  readonly enterSends: boolean;
  /** Обычный вид ленты или компактный (имя и текст в одну строку, без аватаров). */
  readonly density: 'comfortable' | 'compact';
  /** Открывая беседу: с первого непрочитанного / сразу с новых / не отмечать прочитанным само. */
  readonly markRead: 'open' | 'newest' | 'manual';
  /** Порядок каналов и личных сообщений: по алфавиту или по последнему сообщению. */
  readonly sidebarSort: 'alpha' | 'recent';
  readonly showUnreads: boolean;
  readonly showThreads: boolean;
  readonly showDrafts: boolean;
  /** Сообщение только из эмодзи — крупно. */
  readonly jumbomoji: boolean;
  /** «:)» при вводе превращается в 🙂. */
  readonly emoticons: boolean;
  /** Строка форматирования над полем ввода. */
  readonly formattingBar: boolean;
  /** «Оформлять сообщения разметкой» (как в Slack): *жирный* и списки видны знаками и оформляются при отправке. */
  readonly markup: boolean;
  /** В блоке кода ``` Enter переносит строку, а не отправляет (отправить — Shift+Enter). */
  readonly codeBlockEnter: boolean;
  /** Язык интерфейса. */
  readonly language: 'ru' | 'en';
  /** Уведомлять об ответах в тредах, на которые я подписан. */
  readonly notifyThreads: boolean;
  /** Уведомлять, когда в моём канале начинается созвон. */
  readonly notifyHuddles: boolean;
  /** Ключевые слова через запятую — сообщение с ними уведомляет, как упоминание. */
  readonly notifyKeywords: string;
  /** Когда присылать уведомления: всегда или в промежутке notifyFrom–notifyTo каждый день / по будням. */
  readonly notifySchedule: 'always' | 'everyday' | 'weekdays';
  readonly notifyFrom: string;
  readonly notifyTo: string;
  /** Показывать текст сообщения в уведомлении. */
  readonly notifyPreview: boolean;
  readonly notifySound: NotifySound;
  /** Карточка уведомления внутри мессенджера, когда вкладка открыта. */
  readonly notifyInApp: boolean;
  /** Карточка «Пригласить коллег» внизу боковой панели (как в Notion); крестик её убирает. */
  readonly inviteCard: boolean;
  readonly sidebarShow: 'all' | 'unread';
  readonly sidebarActivity: 'all' | 'week' | 'month';
  readonly sidebarGroup: 'sections' | 'none';
  readonly sidebarColor: 'light' | 'dark';
  /** Вид списка «Личные сообщения»: имена с сообщениями или только имена. */
  readonly dmView: 'detailed' | 'names';
  /** За сколько минут напоминать о встрече в личке «Календаря» («0» — не напоминать). */
  readonly calendarReminder: '0' | '5' | '10' | '15' | '30';
  readonly undoSend: '0' | '5' | '10' | '20' | '30';
  /** Показывать встречи в статусе: «На встрече: … до 15:30». */
  readonly calendarStatus: boolean;
  /** Размер текста сообщений в ленте: обычный или крупный. */
  readonly messageSize: 'normal' | 'large';
  /** Шрифт интерфейса: Passwork (по умолчанию, свой, форк Inter), Lato (как в Slack) или системный. */
  readonly font: 'passwork' | 'lato' | 'system';
  /** Быстрые реакции над сообщением: три своих или мои самые частые. */
  readonly reactionMode: 'custom' | 'frequent';
  /** Свои быстрые реакции — эмодзи через пробел; пусто — 👍 ✅ 👀. */
  readonly reactionSet: string;
  /** Сервисы Пассворка в полосе разделов слева — через пробел, по порядку («passwork bridge»). */
  readonly railServices: string;
  /** Разделы, где человеку уже подсветили «?» с обзором (через пробел). */
  readonly sectionGuides: string;
  readonly screenshotKeys: string;
  /** '1' — скриншотер Chat выключен: приложение не занимает сочетания, кнопка в поле ввода скрыта. */
  readonly screenshotsOff: string;
  readonly appKeys: string;
  /** Кнопки панели поля ввода по порядку; пусто — набор по умолчанию, остальные спрятаны в «⋯». */
  readonly composerTools: string;
  /** Ширины панелей и колонок («sidebar=320 panel=420»); на всех устройствах человека. */
  readonly paneWidths: string;
  /** Камера для кружков: как в прошлый раз, всегда передняя или всегда задняя. */
  readonly circleCamera: 'last' | 'user' | 'env';
  /** С какой камеры снимали кружок в прошлый раз (запоминается сама). */
  readonly circleFacing: 'user' | 'env';
  /** Разделы полосы слева по порядку, через пробел; «-» перед скрытым. Пусто — порядок по умолчанию. */
  readonly railItems: string;
  /** Карточки быстрого доступа на «Главной» телефона по порядку, через пробел; «-» перед скрытой. Пусто — как задумано. */
  readonly homeCards: string;
  readonly homeRole: '' | 'sales' | 'dev' | 'support' | 'lead';
  readonly homeWidgets: string;
  readonly homePrompts: string;
  /** Плашка «Перевести на …» над беседой на другом языке (как в Telegram). */
  readonly translateOffer: boolean;
  /** На какой язык переводить; пусто — на язык интерфейса. */
  readonly translateTo: string;
  /** Языки, которые не переводить, — коды через пробел. */
  readonly translateSkip: string;
  /** Пояса календаря: «Europe/Moscow|МСК;Asia/Tokyo», первый — основной; пусто — только пояс устройства. */
  readonly calendarZones: string;
  /** Недавно выбранные пояса, новые первыми. */
  readonly calendarZonesRecent: string;
}

export type PreferenceKey = keyof Preferences;

export const DEFAULT_PREFERENCES: Preferences = {
  enterSends: true,
  density: 'comfortable',
  markRead: 'open',
  sidebarSort: 'alpha',
  showUnreads: true,
  showThreads: true,
  showDrafts: true,
  jumbomoji: true,
  emoticons: true,
  formattingBar: true,
  markup: false,
  codeBlockEnter: true,
  language: 'ru',
  notifyThreads: true,
  notifyHuddles: true,
  notifyKeywords: '',
  notifySchedule: 'always',
  notifyFrom: '09:00',
  notifyTo: '19:00',
  notifyPreview: true,
  notifySound: 'ding',
  notifyInApp: true,
  inviteCard: true,
  sidebarShow: 'all',
  sidebarActivity: 'all',
  sidebarGroup: 'sections',
  sidebarColor: 'light',
  dmView: 'detailed',
  calendarReminder: '10',
  undoSend: '10',
  calendarStatus: true,
  messageSize: 'normal',
  font: 'passwork',
  reactionMode: 'custom',
  reactionSet: '',
  railServices: '',
  sectionGuides: '',
  screenshotKeys: '',
  screenshotsOff: '',
  appKeys: '',
  composerTools: '',
  paneWidths: '',
  circleCamera: 'last',
  circleFacing: 'user',
  railItems: '',
  homeCards: '',
  homeRole: '',
  homeWidgets: '',
  homePrompts: '',
  translateOffer: true,
  translateTo: '',
  translateSkip: '',
  calendarZones: '',
  calendarZonesRecent: '',
};

function isKey(key: string): key is PreferenceKey {
  return Object.hasOwn(PREFERENCE_SCHEMA, key);
}

function valid(key: PreferenceKey, value: unknown): boolean {
  const values: readonly string[] | 'text' | 'long' | 'time' | 'rail' | 'cards' | 'lang' | 'langs' | 'zones' | null = PREFERENCE_SCHEMA[key].values;
  if (values === null) return typeof value === 'boolean';
  if (typeof value !== 'string') return false;
  if (values === 'text') return value.length <= TEXT_LIMIT;
  if (values === 'long') return value.length <= LONG_LIMIT;
  if (values === 'time') return HALF_HOURS.includes(value);
  if (values === 'rail') return validRailItems(value);
  if (values === 'cards') return validHomeCards(value);
  if (values === 'lang') return value === '' || LANGUAGE_CODES.includes(value);
  if (values === 'langs') return validLangs(value);
  if (values === 'zones') return validZoneList(value);
  return values.includes(value);
}

/** Показывать ли в «Оформлении» настройку «Крупные эмодзи». Выключена: эмодзи без текста всегда крупные; значение в настройках хранится и вернётся, если включить. */
export const JUMBOMOJI_SETTING: boolean = false; // eslint-disable-line @typescript-eslint/no-inferrable-types -- флаг, который меняют вручную

/** Показывать ли эмодзи-сообщение крупно: пока настройки на странице нет — всегда, иначе по выбору. */
export const jumbomojiOn = (chosen: boolean): boolean => chosen || !JUMBOMOJI_SETTING;

const flag = (initial: boolean) => z.boolean().catch(initial);
const oneOf = <T extends string>(values: readonly [T, ...T[]], initial: T) => z.enum(values).catch(initial);

/** Схема ответа сервера: неизвестное или испорченное значение заменяется значением по умолчанию. */
export const preferencesSchema = z
  .object({
    enterSends: flag(true),
    density: oneOf(['comfortable', 'compact'], 'comfortable'),
    markRead: oneOf(['open', 'newest', 'manual'], 'open'),
    sidebarSort: oneOf(['alpha', 'recent'], 'alpha'),
    showUnreads: flag(true),
    showThreads: flag(true),
    showDrafts: flag(true),
    jumbomoji: flag(true),
    emoticons: flag(true),
    formattingBar: flag(true),
    markup: flag(false),
    codeBlockEnter: flag(true),
    language: oneOf(['ru', 'en'], 'ru'),
    notifyThreads: flag(true),
    notifyHuddles: flag(true),
    notifyKeywords: z.string().max(TEXT_LIMIT).catch(''),
    notifySchedule: oneOf(['always', 'everyday', 'weekdays'], 'always'),
    notifyFrom: z.string().refine((v) => HALF_HOURS.includes(v)).catch('09:00'),
    notifyTo: z.string().refine((v) => HALF_HOURS.includes(v)).catch('19:00'),
    notifyPreview: flag(true),
    notifySound: oneOf(['ding', 'pop', 'chime', 'knock', 'none'], 'ding'),
    notifyInApp: flag(true),
    inviteCard: flag(true),
    sidebarShow: oneOf(['all', 'unread'], 'all'),
    sidebarActivity: oneOf(['all', 'week', 'month'], 'all'),
    sidebarGroup: oneOf(['sections', 'none'], 'sections'),
    sidebarColor: oneOf(['light', 'dark'], 'light'),
    dmView: oneOf(['detailed', 'names'], 'detailed'),
    calendarReminder: oneOf(['0', '5', '10', '15', '30'], '10'),
    undoSend: oneOf(['0', '5', '10', '20', '30'], '10'),
    calendarStatus: flag(true),
    messageSize: oneOf(['normal', 'large'], 'normal'),
    // Прежний «plex» (IBM Plex Sans) убран из выбора, как и прежний Nunito — читаем как Passwork.
    font: z.preprocess((v) => (v === 'plex' || v === 'nunito' ? 'passwork' : v), z.enum(['passwork', 'lato', 'system']).catch('passwork')),
    reactionMode: oneOf(['custom', 'frequent'], 'custom'),
    reactionSet: z.string().max(TEXT_LIMIT).catch(''),
    railServices: z.string().max(TEXT_LIMIT).catch(''),
    sectionGuides: z.string().max(TEXT_LIMIT).catch(''),
    screenshotKeys: z.string().max(TEXT_LIMIT).catch(''),
    screenshotsOff: z.string().max(TEXT_LIMIT).catch(''),
    appKeys: z.string().max(TEXT_LIMIT).catch(''),
    composerTools: z.string().max(TEXT_LIMIT).catch(''),
    paneWidths: z.string().max(TEXT_LIMIT).catch(''),
    circleCamera: oneOf(['last', 'user', 'env'], 'last'),
    circleFacing: oneOf(['user', 'env'], 'user'),
    railItems: z.string().refine(validRailItems).catch(''),
    homeCards: z.string().refine(validHomeCards).catch(''),
    homeRole: oneOf(['', 'sales', 'dev', 'support', 'lead'], ''),
    homeWidgets: z.string().max(TEXT_LIMIT).catch(''),
    homePrompts: z.string().max(LONG_LIMIT).catch(''),
    translateOffer: flag(true),
    translateTo: z.string().refine((v) => v === '' || LANGUAGE_CODES.includes(v)).catch(''),
    translateSkip: z.string().refine(validLangs).catch(''),
    calendarZones: z.string().refine(validZoneList).catch(''),
    calendarZonesRecent: z.string().refine(validZoneList).catch(''),
  })
  .catch(DEFAULT_PREFERENCES);

/** Сохранённое поверх значений по умолчанию; мусор отбрасываем. */
export function resolvePreferences(stored: unknown): Preferences {
  return preferencesSchema.parse(stored ?? {});
}

/** Проверка изменения так же, как на сервере: текст ошибки или null. */
export function preferenceError(patch: Readonly<Record<string, unknown>>): string | null {
  const entries = Object.entries(patch);
  if (entries.length === 0) return t('Нечего сохранять');
  for (const [key, value] of entries) {
    if (!isKey(key)) return t('Неизвестная настройка «{key}»', { key });
    if (!valid(key, value)) return t('Недопустимое значение настройки «{key}»', { key });
  }
  return null;
}
